import {useEffect, useState} from 'react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Badge} from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {toast} from 'sonner';
import {Plus, Edit, Trash2} from 'lucide-react';
import {getTiers, createTier, updateTier, deleteTier} from '@/api/affiliate.api';

const initialForm = {
    title: '',
    name: '',
    fromAmount: '',
    toAmount: '',
    cashbackPercent: '',
    maxCashback: '',
    status: true
};

const TiersTab = () => {
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(initialForm);

    const load = async () => {
        setLoading(true);
        try {
            const res = await getTiers();
            setTiers(res.data);
        } catch {
            toast.error('Failed to load tiers');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await getTiers();
                if (mounted) setTiers(res.data);
            } catch {
                if (mounted) toast.error('Failed to load tiers');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    // --- Form handlers ---
    function openAdd() {
        setEditId(null);
        setForm(initialForm);
        setOpen(true);
    }

    function openEdit(t) {
        setEditId(t.id);
        setForm({
            title: t.title || '',
            name: t.name || '',
            fromAmount: t.fromAmount ?? '',
            toAmount: t.toAmount ?? '',
            cashbackPercent: t.cashbackPercent ?? '',
            maxCashback: t.maxCashback ?? '',
            status: !!t.status
        });
        setOpen(true);
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            title: form.title.trim(),
            name: form.name.trim(),
            fromAmount: Number(form.fromAmount) || 0,
            toAmount: Number(form.toAmount) || 0,
            cashbackPercent: Number(form.cashbackPercent) || 0,
            maxCashback: Number(form.maxCashback) || 0,
            status: !!form.status
        };
        try {
            if (editId) {
                await updateTier(editId, payload);
                toast.success('Tier updated');
            } else {
                await createTier(payload);
                toast.success('Tier created');
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm('Delete this tier?')) return;
        try {
            await deleteTier(id);
            toast.success('Tier deleted');
            load();
        } catch {
            toast.error('Delete failed');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full"/>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Tier System</h2>
                <Button onClick={openAdd} data-testid="tier-add-button">
                    <Plus className="mr-2" size={18}/> Add Tier
                </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                    <thead className="bg-gray-100">
                    <tr>
                        <th className="p-3 text-left">Title</th>
                        <th className="p-3 text-left">Name</th>
                        <th className="p-3 text-right">From Amount</th>
                        <th className="p-3 text-right">To Amount</th>
                        <th className="p-3 text-right">Cashback %</th>
                        <th className="p-3 text-right">Max Cashback</th>
                        <th className="p-3 text-center">Status</th>
                        <th className="p-3 text-right">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {tiers.map((t) => (
                        <tr key={t.id} className="border-t hover:bg-gray-50">
                            <td className="p-3 font-medium">{t.title}</td>
                            <td className="p-3">{t.name}</td>
                            <td className="p-3 text-right">₹{t.fromAmount}</td>
                            <td className="p-3 text-right">₹{t.toAmount}</td>
                            <td className="p-3 text-right">{t.cashbackPercent}%</td>
                            <td className="p-3 text-right">₹{t.maxCashback}</td>
                            <td className="p-3 text-center">
                                <Badge
                                    className={t.status ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}>
                                    {t.status ? 'Active' : 'Inactive'}
                                </Badge>
                            </td>
                            <td className="p-3 text-right flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEdit(t)}
                                        data-testid={`tier-edit-${t.id}`}>
                                    <Edit size={14}/>
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => onDelete(t.id)}
                                        data-testid={`tier-delete-${t.id}`}>
                                    <Trash2 size={14}/>
                                </Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                </div>
                {tiers.length === 0 && (
                    <p className="text-center text-gray-500 p-8">No tiers yet. Click &quot;Add Tier&quot; to create one.</p>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editId ? 'Edit Tier' : 'Create Tier'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={onSubmit} className="space-y-4">
                        <div>
                            <Label>Title *</Label>
                            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                                   required data-testid="tier-title-input"/>
                        </div>
                        <div>
                            <Label>Name *</Label>
                            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                                   required data-testid="tier-name-input"/>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>From Amount</Label>
                                <Input type="number" min="0" value={form.fromAmount}
                                       onChange={(e) => setForm({...form, fromAmount: e.target.value})}
                                       data-testid="tier-from-input"/>
                            </div>
                            <div>
                                <Label>To Amount</Label>
                                <Input type="number" min="0" value={form.toAmount}
                                       onChange={(e) => setForm({...form, toAmount: e.target.value})}
                                       data-testid="tier-to-input"/>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Cashback (%)</Label>
                                <Input type="number" min="0" max="100" step="0.01" value={form.cashbackPercent}
                                       onChange={(e) => setForm({...form, cashbackPercent: e.target.value})}
                                       data-testid="tier-cashback-input"/>
                            </div>
                            <div>
                                <Label>Max Cashback (₹)</Label>
                                <Input type="number" min="0" value={form.maxCashback}
                                       onChange={(e) => setForm({...form, maxCashback: e.target.value})}
                                       data-testid="tier-maxcashback-input"/>
                            </div>
                        </div>
                        <div>
                            <Label>Status *</Label>
                            <Select value={String(form.status)}
                                    onValueChange={(v) => setForm({...form, status: v === 'true'})}>
                                <SelectTrigger><SelectValue/></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="true">Active</SelectItem>
                                    <SelectItem value="false">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button type="submit" className="w-full" data-testid="tier-submit-button">
                            {editId ? 'Update Tier' : 'Create Tier'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TiersTab;
